// MARK://dhk8/admin-shell/components/EntityDetailPage
// "View-first" detail shell for an entity record. It is a pure layout frame: the
// page owns its data and passes presentational bits in as ReactNode SLOTS, so
// this component never couples to StatusPill / Tabs / StatCard (which may still be
// in construction elsewhere).
//
// Layout:
//   ┌───────────────────────────────────────────────┐
//   │ [image]  Title              pills    actions   │  ← header
//   │          Subtitle                              │
//   ├───────────────────────────────────────────────┤
//   │ kpis strip (full width)                        │  ← optional
//   ├───────────────────────────────────────────────┤
//   │ tabs                                           │  ← optional
//   │ children (body)                                │
//   └───────────────────────────────────────────────┘
//
// Self-contained: imports nothing but React + tokens (via scoped `nui-*` CSS).
import type { ReactNode } from 'react'

const ENTITY_DETAIL_CSS = `
.nui-edp { display: flex; flex-direction: column; gap: var(--pos-space-4, 16px); }
.nui-edp__header {
  display: flex; align-items: flex-start; gap: var(--pos-space-4, 16px);
  flex-wrap: wrap;
}
.nui-edp__media {
  width: 72px; height: 72px; flex-shrink: 0;
  border-radius: var(--pos-radius-lg, 12px);
  object-fit: cover;
  background: var(--pos-elevated);
  border: 1px solid var(--pos-separator-strong);
}
.nui-edp__media--placeholder {
  display: flex; align-items: center; justify-content: center;
  color: var(--pos-fg-tertiary);
  font-family: var(--font-display, var(--font-sans));
  font-size: var(--pos-text-2xl, 24px);
  font-weight: var(--pos-weight-semibold, 600);
}
.nui-edp__heading { flex: 1; min-width: 200px; display: flex; flex-direction: column; gap: 2px; }
.nui-edp__title {
  font-family: var(--font-display, var(--font-sans));
  font-size: var(--pos-text-2xl, 24px);
  font-weight: var(--pos-weight-semibold, 600);
  line-height: var(--pos-leading-tight, 1.15);
  color: var(--pos-fg);
  margin: 0;
}
.nui-edp__subtitle {
  font-size: var(--pos-text-sm, 13px);
  color: var(--pos-fg-tertiary);
  margin: 0;
}
.nui-edp__pills { display: flex; flex-wrap: wrap; gap: var(--pos-space-2, 8px); margin-top: 4px; }
.nui-edp__actions {
  display: flex; align-items: center; gap: var(--pos-space-2, 8px);
  flex-wrap: wrap; margin-left: auto;
}
.nui-edp__kpis {
  display: flex; flex-wrap: wrap; gap: var(--pos-space-3, 12px);
  padding: var(--pos-space-3, 12px) 0;
  border-top: 1px solid var(--pos-separator);
  border-bottom: 1px solid var(--pos-separator);
}
.nui-edp__tabs { margin-bottom: var(--pos-space-1, 4px); }
.nui-edp__body { display: flex; flex-direction: column; gap: var(--pos-space-4, 16px); }
`

export interface EntityDetailPageProps {
  title: string
  subtitle?: string
  /** Image URL for the entity; when absent a lettered placeholder is shown. */
  image?: string | null
  pills?: ReactNode
  kpis?: ReactNode
  actions?: ReactNode
  tabs?: ReactNode
  children?: ReactNode
}

export default function EntityDetailPage({
  title,
  subtitle,
  image,
  pills,
  kpis,
  actions,
  tabs,
  children,
}: EntityDetailPageProps) {
  const initial = title.trim().charAt(0).toUpperCase() || '?'
  return (
    <div className="nui-edp">
      <style>{ENTITY_DETAIL_CSS}</style>

      <div className="nui-edp__header">
        {image ? (
          <img className="nui-edp__media" src={image} alt="" />
        ) : (
          <div className="nui-edp__media nui-edp__media--placeholder" aria-hidden>
            {initial}
          </div>
        )}

        <div className="nui-edp__heading">
          <h1 className="nui-edp__title">{title}</h1>
          {subtitle ? <p className="nui-edp__subtitle">{subtitle}</p> : null}
          {pills ? <div className="nui-edp__pills">{pills}</div> : null}
        </div>

        {actions ? <div className="nui-edp__actions">{actions}</div> : null}
      </div>

      {kpis ? <div className="nui-edp__kpis">{kpis}</div> : null}

      {tabs ? <div className="nui-edp__tabs">{tabs}</div> : null}

      <div className="nui-edp__body">{children}</div>
    </div>
  )
}
